import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type CashFlow, type MonthFlow } from "../api.js";
import { Money } from "./Money.js";
import { StatCard } from "./StatCard.js";
import { Card } from "./ui/card.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table.js";

const STAT_GRID = "grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4";

/** "YYYY-MM" → "Jul 2026" (UTC, no off-by-one from the day). */
function monthLabel(key: string): string {
  return new Date(`${key}-01T00:00:00Z`).toLocaleString(undefined, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** One month's spend headline with income / net beneath. */
function SpendCard({ label, flow }: { label: string; flow: MonthFlow }): JSX.Element {
  return (
    <StatCard
      label={label}
      value={<Money amountMinor={flow.spend_eur_minor} native="EUR" block />}
      sub={
        <>
          in <Money amountMinor={flow.income_eur_minor} native="EUR" /> · net{" "}
          <Money amountMinor={flow.net_eur_minor} native="EUR" />
        </>
      }
    />
  );
}

export function MonthlySpend(): JSX.Element {
  const cf = useQuery<CashFlow>({ queryKey: ["cash-flow"], queryFn: () => api.cashFlow() });

  return (
    <>
      <h3 className="mt-8 mb-4 text-lg font-semibold">Monthly spend</h3>
      {!cf.data ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : !cf.data.has_spend_data ? (
        <Card className="p-5">
          <span className="text-muted-foreground text-sm">
            No spend transactions yet. Add cash spend on the Transactions page (“+ Add” → Spend) —
            these numbers come alive as soon as there's real data. Broker/investment activity is
            excluded from spend by design.
          </span>
        </Card>
      ) : (
        <>
          <div className={STAT_GRID}>
            <SpendCard label="This month (so far)" flow={cf.data.current_month} />
            {cf.data.last_months.map((m) => (
              <SpendCard key={m.month} label={monthLabel(m.month)} flow={m} />
            ))}
            <SpendCard label="12-mo avg / month" flow={{ month: "avg", ...cf.data.trailing_12_avg }} />
          </div>

          {cf.data.by_category.length > 0 && (
            <Card className="mt-4 overflow-hidden p-0">
              <div className="text-muted-foreground px-4 pt-4 text-xs font-medium tracking-wide uppercase">
                Spend by category · last 3 full months
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cf.data.by_category.map((row) => (
                    <TableRow key={row.category_id ?? "uncategorised"}>
                      <TableCell>{row.category_name}</TableCell>
                      <TableCell className="text-right font-medium">
                        <Money amountMinor={row.spend_eur_minor} native="EUR" />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </>
  );
}
